import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, Observable, Subject, tap, throwError, timeout, TimeoutError } from 'rxjs';
import {
  AuthenticationProvider,
  environment,
  ENVIRONMENT_CONFIG,
  MovinmotionAppEnum,
  User,
} from '@movinmotion/conf-shared';
import { HttpClient } from '@angular/common/http';
import { concatUrl } from '@movinmotion/util-functions';
import { CookieService } from '../cookie/cookie.service';
import { CacheService } from '../cache/cache.service';
import { MixpanelService, updateSentryUser } from '@movinmotion/data-access-third-party';
import { legacyDates } from '@movinmotion/util-ngx';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements AuthenticationProvider {
  private readonly movinmotionBackendUrl = inject(ENVIRONMENT_CONFIG).api.movinmotionBackend;
  private readonly apiLoginUrl = concatUrl(this.movinmotionBackendUrl, 'login');

  private readonly http = inject(HttpClient);
  private readonly cookie = inject(CookieService);
  private readonly cache = inject(CacheService);
  private readonly mixpanel = inject(MixpanelService);

  readonly loggingOut$: Subject<void> = new Subject<void>();

  private _loggedUser$!: Observable<User>;

  /**
   * Redirection to the common and shared login screen
   *
   * @param options allowed option for the redirection
   */
  redirectToLogin(
    options: {
      returnUrl?: string;
      resetPasswordMode?: boolean;
      changePasswordMode?: boolean;
      filledEmail?: string;
      sessionExpired?: boolean;
      display?: string;
    } | null = null,
  ): void {
    const { url, queryParamKeys } = environment.authentication;
    const authDestination = new URL(url);
    authDestination.searchParams.set(queryParamKeys.redirectUrl, options?.returnUrl || window.location.href);
    if (options?.resetPasswordMode) {
      authDestination.searchParams.set(queryParamKeys.resetPassword, 'true');
    }
    if (options?.filledEmail) {
      authDestination.searchParams.set(queryParamKeys.email, options.filledEmail);
    }
    if (options?.changePasswordMode) {
      authDestination.searchParams.set(queryParamKeys.changePassword, 'true');
    }
    if (options?.display) {
      authDestination.searchParams.set(queryParamKeys.display, options?.display);
    }
    window.location.href = authDestination.toString();
  }

  /**
   * Login the user using an authentication token (provided by the shared login screen {@see redirectToLogin})
   *
   * @param body with the token and the application to connect to
   */
  login$(body: { token: string; entropy?: number; appName?: MovinmotionAppEnum }): Observable<User> {
    return this.http
      .post<User>(this.apiLoginUrl, body, {
        context: legacyDates(),
      })
      .pipe(
        tap((user: User) => {
          this.onLogonChange();
          updateSentryUser(user);
          this.mixpanel.onUserChange({ user, origin: 'MOVINMOTION' });
          this.mixpanel.track({ eventName: 'Connexion' });
        }),
        timeout(90000),
        catchError(error => {
          if (error instanceof TimeoutError) {
            location.reload();
            return EMPTY;
          }

          throw error;
        }),
      );
  }

  /**
   * Logout the current user, will remove the M_SESSION cookie
   */
  logout$(): Observable<void> {
    this.loggingOut$.next();
    return this.http.delete<void>(this.apiLoginUrl).pipe(
      tap(() => {
        this.onLogout();
      }),
    );
  }

  get loggedUser$(): Observable<User> {
    if (!this._loggedUser$) {
      this._loggedUser$ = this.getLoggedUser$();
    }
    return this._loggedUser$;
  }

  /**
   * Get current user logged information
   */
  getLoggedUser$(): Observable<User> {
    return this.http.get<User>(this.apiLoginUrl).pipe(
      tap(user => {
        if (user) {
          this.cache.cachingOnUserChanged();
          this.mixpanel.onUserChange({ user, origin: 'MOVINMOTION' });
        }
      }),
      catchError(err => {
        this.onLogout();
        this.redirectToLogin();
        return throwError(() => err);
      }),
    );
  }

  private onLogout(): void {
    this.mixpanel.onLogout();
    this.onLogonChange();
    this.cookie.suppressAuthCookie();
  }

  private onLogonChange(): void {
    this.cache.userChanged$.next(true);
    this.cache.companyChanged$.next(true);
  }
}
